import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { modalRoot } from '@/components/Modal';

const OFFSET = 8;
const EDGE_PADDING = 16;

/**
 * Hover state plus fixed-position coordinates for a tooltip rendered into
 * `#modals-root`, so it escapes any `overflow: hidden` ancestor.
 *
 * The box sits below the trigger, flipping above it when the viewport has no
 * room below. Attach `tooltipRef` to the box so its height can be measured —
 * the first render lands below, the flip is applied before paint.
 *
 * `position` is null until the trigger has been measured, and while hidden.
 */
const useAnchoredTooltip = <T extends HTMLElement>(width: number) => {
  const [show, setShow] = useState(false);
  const [height, setHeight] = useState(0);
  const triggerRef = useRef<T>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const portalRef = useRef(document.createElement('div'));

  useEffect(() => {
    // The box is position: fixed, so any scroll would detach it from its trigger.
    const hide = () => setShow(false);
    window.addEventListener('scroll', hide, true);
    return () => window.removeEventListener('scroll', hide, true);
  }, []);

  useEffect(() => {
    const el = portalRef.current;
    modalRoot?.appendChild(el);
    return () => {
      modalRoot?.removeChild(el);
    };
  }, []);

  // Measured before paint on every show, and again whenever the content
  // resizes while open, so the flip decision below never uses a stale height.
  useLayoutEffect(() => {
    const el = tooltipRef.current;
    if (!el) return;

    const measure = () => setHeight(el.offsetHeight);
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [show]);

  const rect = triggerRef.current?.getBoundingClientRect();

  const spaceBelow = rect ? window.innerHeight - rect.bottom - OFFSET : 0;
  const spaceAbove = rect ? rect.top - OFFSET : 0;
  // Height is 0 on the very first render — default to below until measured.
  const above = !!height && spaceBelow < height && spaceAbove > spaceBelow;

  const position =
    show && rect
      ? {
          left: Math.max(
            EDGE_PADDING,
            Math.min(rect.x, window.innerWidth - width - EDGE_PADDING),
          ),
          top: above
            ? Math.max(EDGE_PADDING, rect.top - OFFSET - height)
            : rect.bottom + OFFSET,
        }
      : null;

  return {
    triggerRef,
    tooltipRef,
    portalEl: portalRef.current,
    position,
    triggerProps: {
      onMouseEnter: () => setShow(true),
      onMouseLeave: () => setShow(false),
    },
  };
};

export default useAnchoredTooltip;
